"use client";

import React, { useActionState, useEffect, useRef, useState } from "react";
import DocumentCard from "../DocumentCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleDown } from "@fortawesome/sharp-light-svg-icons";
import {
  CaseData,
  CaseDocument,
  CMSData,
  CmsPage,
  CMSSection,
} from "@/cms/models";
import {
  DOWNLOAD_INITIAL_STATE,
  getConsentText,
} from "@/services/form.service";
import { getDownloadZipUrl, submitDownloadForm } from "@/actions/download-form";
import { trackCase } from "@/actions/track-case";
import { formatBytes } from "@/utils/helper";
import TextInput from "../ui/inputs/TextInput";
import TextAreaInput from "../ui/inputs/TextAreaInput";
import FormLayout from "../ui/inputs/FormLayout";
import CheckboxInput from "../ui/inputs/CheckboxInput";
import ZodErrors from "../ui/ZodErrors";
import { useSession } from "@/hooks/useSession";
import { ApplicationDataType } from "@/services/queries/get-application-details";
import RippleCtaButton from "../ui/RippleCtaButton";

const DownloadDocumentForm: React.FC<{
  appData: ApplicationDataType;
  cmsData: CMSData;
  pageData: CmsPage;
  caseData: CaseData;
  cmsSection: CMSSection<{
    showAgent?: boolean;
    downloadFormTextColor?: string;
  }>;
}> = ({ cmsData, pageData, caseData, cmsSection }) => {
  const sessionKey = `form-filled-${caseData.id}`;
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [isValid, setIsValid] = useState(false);
  const [downloadAll, setDownloadAll] = useState(false);
  const [formState, formAction, isPending] = useActionState(
    (prevState: any, formData: FormData) => {
      formData.append("caseId", caseData!.id!);
      return submitDownloadForm(prevState, formData);
    },
    DOWNLOAD_INITIAL_STATE,
  );
  const [selectedDocument, setSelectedDocument] = useState<CaseDocument | null>(
    null,
  );
  const [hasFilledForm, setHasFilledForm] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [allowContact, setAllowContact] = useState(false);
  const { getSessionItem, setSessionItem } = useSession();

  useEffect(() => {
    const wasFormFilled = getSessionItem(sessionKey);
    if (wasFormFilled) {
      setHasFilledForm(true);
    }
  }, [caseData.id]);

  useEffect(() => {
    const handleFormSuccess = async () => {
      if (isValid) {
        setSessionItem(sessionKey, "true");
        setHasFilledForm(true);

        if (downloadAll) {
          await downloadAllDocuments(caseData!.documents);
        } else {
          await downloadSingleDocument(selectedDocument!);
        }
        setDownloadAll(false);
        setShowForm(false);
        setSelectedDocument(null);
        setIsValid(false);
      }
    };
    handleFormSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  useEffect(() => {
    if (formState.isSuccess ?? false) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [formState]);

  useEffect(() => {
    if (showForm) {
      formContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [showForm]);

  const { firstName, lastName, address, zip, city, phone, email, message } =
    formState?.data || {};

  const downloadAllDocuments = async (casedocuments: CaseDocument[]) => {
    if (!window) {
      return;
    }

    const blob = await getDownloadZipUrl(
      caseData.advertisementAddressOrDisplayName,
      caseData!.id!,
      casedocuments.map((x) => x.id),
    );

    window?.pageVisitTracker?.track("DocumentDownload", {
      caseId: caseData.id,
    });

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute(
      "download",
      `${caseData.advertisementAddressOrDisplayName}.zip`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const downloadSingleDocument = async (casedocument: CaseDocument) => {
    if (!window) {
      return;
    }

    const response = await fetch(casedocument.url);

    if (response.ok) {
      await trackCase(caseData.id, "dk.realequity.stat.downloaded");
      window?.pageVisitTracker?.track("DocumentDownload", {
        caseId: caseData.id,
      });
    }

    const blob = await response.blob();

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", casedocument.fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadClick = (document: CaseDocument | null) => {
    if (!pageData.downloadForm || hasFilledForm) {
      if (document) {
        downloadSingleDocument(document);
      } else {
        downloadAllDocuments(caseData!.documents);
      }
    } else {
      setShowForm(true);
      setSelectedDocument(document);
      setDownloadAll(!document);
    }
  };

  return (
    <div ref={formContainerRef} className="flex flex-col gap-4">
      {showForm ? (
        <FormLayout
          backLabel="Tilbage"
          onBack={() => {
            setShowForm(false);
            setSelectedDocument(null);
          }}
          submitLabel="Få adgang"
          isPending={isPending}
          featureFlags={cmsData?.featureFlags}
          formAction={formAction}
        >
          <TextInput
            cmsData={cmsData}
            name="firstName"
            label="Fornavn"
            defaultValue={firstName}
            errors={formState?.zodErrors?.firstName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            name="lastName"
            label="Efternavn"
            defaultValue={lastName}
            errors={formState?.zodErrors?.lastName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-6"
            name="address"
            label="Adresse"
            defaultValue={address}
            errors={formState?.zodErrors?.address}
            required={cmsData?.forms.addressRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-2"
            name="zip"
            label="Postnr"
            defaultValue={zip}
            errors={formState?.zodErrors?.zip}
            required={cmsData?.forms.zipRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-4"
            name="city"
            label="By"
            defaultValue={city}
            errors={formState?.zodErrors?.city}
            required={cmsData?.forms.cityRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="phone"
            label="Telefonnummer"
            defaultValue={phone}
            errors={formState?.zodErrors?.phone}
            required={cmsData?.forms.phoneRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="email"
            label="E-mail"
            defaultValue={email}
            errors={formState?.zodErrors?.email}
            required={cmsData?.forms.emailRequired}
          />
          <TextAreaInput
            cmsData={cmsData}
            name="message"
            label="Besked"
            defaultValue={message}
            errors={formState?.zodErrors?.message}
            required={cmsData?.forms.messageRequired}
          />
          <div className="col-span-6 flex flex-col">
            <CheckboxInput
              isChecked={allowContact}
              label={getConsentText(cmsData)}
              cmsData={cmsData}
              name="allowContact"
              wrap
              onChanged={setAllowContact}
            />
            <ZodErrors errors={formState?.zodErrors?.allowContact} />
          </div>
        </FormLayout>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-4">
            {caseData!.documents.map((document, index) => (
              <DocumentCard
                onClick={() => handleDownloadClick(document)}
                key={index}
                document={document}
                textColor={cmsSection.specifics.downloadFormTextColor}
              ></DocumentCard>
            ))}
          </div>
          <div className="flex flex-col md:hidden gap-2 cursor-pointer">
            {caseData!.documents.map((document, index) => (
              <div
                key={`${document.id}-${index}`}
                onClick={() => handleDownloadClick(document)}
                className="flex justify-between gap-4 py-2"
              >
                <div className="truncate underline underline-offset-2">
                  {document.displayName}
                </div>
                <div className="flex-shrink-0">
                  {formatBytes(document.size)}
                </div>
              </div>
            ))}
          </div>
          <div
            className={`hidden ${caseData?.documents?.length > 1 ? "md:flex" : ""}`}
          >
            <RippleCtaButton onClick={() => handleDownloadClick(null)}>
              <span className="flex items-center gap-2">
                <FontAwesomeIcon className="size-4" icon={faCircleDown} />
                Download alle dokumenter
              </span>
            </RippleCtaButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadDocumentForm;
